import React, { useContext, useCallback } from 'react';
import { path } from 'ramda';
import { makeStyles } from 'tss-react/mui';
import { getAttributeInfo } from '../../../../../../Model/avenyModelUtils';
import { ModelContext } from '../../../../../../Model/ModelContext/ModelContext';
import { IClientBusinessModel } from '../../../../../../Types/ClientModelTypes';
import { attributeDefToDataPath } from '../../../../../../Utils/dataUtils';
import { ValueType } from '../../../../../../Utils/Formatters/FormatterTypes';
import { INodeData } from '../../../../../../Components/QueryComponents/NodeQuery/NodeQuery';
import IntegratedAssociationListItemContent from './IntegratedAssociationListItemContent';
import EDView from '../../../../../../BaseComponents/View/EDView';
import {
  BusinessDataFormContext,
  IBusinessDataFormContext,
} from '../BusinessDataForm/BusinessDataForm';
import {
  IIntegratedContentName,
  IOnDeleteIntegratedAssociation,
} from './IntegratedAssociationList';
import { ISuggestion } from '../../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import EDIconButton from '../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import { InternalError } from '../../../../../../Components/InternalError/InternalError';
import BusinessDataListView, {
  IDataListValue,
} from '../BusinessDataListView/BusinessDataListView';
import {
  getAssociationValue,
  getAssociationLabel,
} from '../../../../../../Model/BusinessModelUtils';
import { IBusinessColorConfig } from '../../../Const/BusinessConfigs';
import EDExpansionPanel from '../../../../../../BaseComponents/Accordion/EDExpansionPanel';
import { darken } from '@mui/material/styles';

interface IProps {
  labelAttributeNames: readonly IIntegratedContentName[];
  contentAttributeNames: readonly IIntegratedContentName[];
  data: INodeData;
  editedValues?: INodeData;
  associatedCollection: IClientBusinessModel;
  onUpdate: (valueObject: INodeData, association?: string) => void;
  onNewUpdate?: (value: any, association?: string) => void;
  onDelete: (value: IOnDeleteIntegratedAssociation) => void;
  associationName: string;
  label: IIntegratedContentName;
  colors: IBusinessColorConfig[];
  expandable?: boolean;
  disableDivider?: boolean;
}

const useStyles = makeStyles()(theme => ({
  container: {},
  containerNonEditMode: {
    margin: 0,
    '&:not(:last-of-type) [class*="MuiAccordion-root"]': {
      borderBottom: `1px solid ${theme.customProperties.Fade.DarkOnLight.divider}`,
    },
  },
  divider: {
    backgroundClip: 'padding-box',
  },
  displayNone: {
    display: 'none',
  },
  headerContainer: {
    width: '100%',
    position: 'relative',
    fontSize: theme.typography.pxToRem(16),
  },
  headerLabelContainer: {
    fontSize: theme.typography.pxToRem(12),
    paddingBottom: 6,
  },
  innerContainer: {
    display: 'flex',
    width: '100%',
  },
  inheritWidth: {
    width: 'inherit',
    padding: theme.spacing(0, 1),
  },
  editContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    margin: theme.spacing(1.5, 0, 1),
    padding: theme.spacing(1, 0),
    position: 'relative',
  },
  closeButtonContainer: {
    position: 'absolute',
    top: -15,
    left: -15,
  },
  removeButton: {
    position: 'relative',
    padding: 3,
    background: theme.palette.background.default,
    border: `1px solid ${theme.customProperties.Fade.DarkOnLight.divider}`,
    '&:hover': {
      background: darken(theme.palette.background.default, 0.05),
    },
  },
  expandableList: {
    background: theme.palette.grey[50],
    paddingLeft: 5,
  },
  listItemContent: {
    flex: 1,
    paddingRight: 16,
    paddingLeft: 16,
  },
}));

const IntegratedAssociationListItem = (props: IProps) => {
  const { classes, cx } = useStyles();
  const { avenyCollectionMap } = useContext(ModelContext);
  const formContext = useContext<IBusinessDataFormContext>(
    BusinessDataFormContext
  );

  const listItemValue = useCallback(
    (content: IIntegratedContentName): string => {
      const dataValue = path<ValueType>(
        attributeDefToDataPath(content.name),
        props.data
      );
      const editedValue = path<ValueType>(
        attributeDefToDataPath(content.name),
        props.editedValues
      );
      return (editedValue ?? dataValue) as string;
    },
    [props.data, props.editedValues]
  );

  const contentTolistItem = useCallback(
    (content: IIntegratedContentName): IDataListValue => {
      const name = getAssociationLabel(content, props.associatedCollection);
      const value = listItemValue(content);
      const attribute = getAttributeInfo(
        props.associatedCollection,
        content.name,
        avenyCollectionMap
      ).attribute;
      return {
        name: name,
        value: value as string,
        attribute: attribute,
      };
    },
    [avenyCollectionMap, listItemValue, props.associatedCollection]
  );

  if (!formContext) {
    return (
      <InternalError
        id={'dbe40e79-fbf7-4cd0-85d0-e36cda97afbc'}
        message="IntegratedAssociationListItem kan inte visas utanför BusinessDataFormContext"
      />
    );
  }

  const onAssociationSelected = (
    suggestion: ISuggestion | null,
    associationName: string
  ) => {
    if (props.onNewUpdate) {
      props.onNewUpdate(
        { id: props.data.id, [associationName]: suggestion?.value ?? '' },
        associationName
      );
    } else {
      props.onUpdate(
        {
          id: props.data.id,
          _version: props.data._version,
          [associationName]: suggestion?.value ?? '',
        },
        associationName
      );
    }
  };

  const onDeleteClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    if (props.data._version !== undefined) {
      props.onDelete({
        id: props.data.id,
        _version: props.data._version,
      });
    }
  };

  const onUpdate = (data: INodeData) => {
    if (props.onNewUpdate) {
      const { id, _version, ...rest } = data;
      props.onNewUpdate({ id, ...rest });
    } else {
      props.onUpdate(data);
    }
  };

  const headerAttribute = getAttributeInfo(
    props.associatedCollection,
    props.label.name,
    avenyCollectionMap
  ).attribute;

  // If item is removed in current form state but form is not yet saved
  const preliminaryRemoved =
    formContext.formState.values &&
    formContext.formState.values._Associations &&
    formContext.formState.values._Associations[
      `${props.associationName}_Delete_Values`
    ]
      ? formContext.formState.values._Associations[
          `${props.associationName}_Delete_Values`
        ].some((value: any) => value.id === props.data.id)
      : false;
  const expandable = !formContext.formState.editMode || props.expandable;
  const content = (
    <div
      className={cx(classes.innerContainer, {
        [classes.editContainer]: formContext.formState.editMode && !expandable,
      })}
    >
      {formContext.formState.editMode && !expandable && (
        <div className={classes.closeButtonContainer}>
          <EDIconButton
            iconSize="medium"
            tooltip={`Ta bort ${props.associatedCollection.RootType.SOLabel}`}
            icon="close"
            className={classes.removeButton}
            onClick={onDeleteClick}
            disableTabbing
            margin="none"
          />
        </div>
      )}
      <div
        className={cx(classes.inheritWidth, {
          [classes.listItemContent]: !expandable,
        })}
      >
        <IntegratedAssociationListItemContent
          contentAttributeNames={
            formContext.formState.editMode && !expandable
              ? [
                  props.label,
                  ...props.labelAttributeNames,
                  ...props.contentAttributeNames,
                ]
              : props.contentAttributeNames
          }
          associatedCollection={props.associatedCollection}
          associationName={props.associationName}
          modelData={props.data}
          avenyCollectionMap={avenyCollectionMap}
          data={props.data}
          editedValues={props.editedValues}
          onUpdate={onUpdate}
          onAssociationSelected={onAssociationSelected}
        />
      </div>
    </div>
  );

  return (
    <div
      className={cx(classes.container, {
        [classes.containerNonEditMode]: !formContext.formState.editMode,
        [classes.displayNone]: preliminaryRemoved,
        [classes.divider]: !props.disableDivider,
      })}
    >
      {expandable && (
        <EDExpansionPanel
          startExpanded={Boolean(props.onNewUpdate)}
          label={
            <EDView
              className={classes.headerContainer}
              key={headerAttribute.AttributeName}
            >
              <BusinessDataListView
                collection={props.associatedCollection}
                label={{
                  name: getAssociationLabel(
                    props.label,
                    props.associatedCollection
                  ),
                  value: getAssociationValue(
                    props.label.name,
                    props.editedValues ?? formContext.formState.values,
                    'IDString',
                    props.data ?? formContext.data,
                    props.label.association
                  ),
                }}
                values={props.labelAttributeNames.map(contentTolistItem)}
                numberOfColumns={props.labelAttributeNames.length}
                listItemRightPadding={0}
                colors={props.colors}
                disableNegativeMargin
              />
            </EDView>
          }
        >
          {content}
        </EDExpansionPanel>
      )}
      {!expandable && content}
    </div>
  );
};

export default IntegratedAssociationListItem;
